import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight, ArrowLeft, FileText, Loader2, AlertCircle, Inbox, X,
} from 'lucide-react';
import { useMyDocumenti, MyDocumento } from '../../hooks/useMyDocumenti';
import { numeroDocumento } from '../../utils/numeroDocumento';

/** Formatta una data ISO (YYYY-MM-DD) in gg/mm/aaaa. */
function fmtIt(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d + '/' + m + '/' + y;
}

const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/** Selezione del documento da cui aprire un reso (Reso da Documenti,
 * ``CONTEXT.md``): Fatture immediate, Fatture differite e D.D.T. non ancora
 * fatturati degli ultimi 12 mesi, dalla chiamata propria dei resi
 * (``/api/clienti/me/rma/documenti``). Un D.D.T. fatturato si rende dalla sua
 * fattura. La finestra è fissa, e la dice il backend. */

function etichetta(doc: MyDocumento): string {
  return doc.tipoRk === 'B' ? `D.D.T. ${numeroDocumento(doc.numero)}` : `Fattura ${numeroDocumento(doc.numero)}`;
}

function Importo({ total }: { total: number | null }) {
  if (total === null) {
    return <span title="Totale in arrivo dal gestionale"
      className="inline-block h-4 w-16 rounded bg-muted animate-pulse align-middle" />;
  }
  return <>€ {total.toFixed(2).replace('.', ',')}</>;
}
export default function RMADocumentSelectPage() {
  const navigate = useNavigate();
  const [annoFilter, setAnnoFilter] = useState<number | ''>('');
  const [meseFilter, setMeseFilter] = useState<number | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 25;

  const { data, loading, error, reload } = useMyDocumenti({
    percorso: '/api/clienti/me/rma/documenti',
    page: currentPage,
    pageSize,
    anno: annoFilter || undefined,
    mese: meseFilter || undefined,
  });
  const finestraMesi = data?.finestra_mesi ?? 12;
  const inArrivo = data?.caricamento?.stato === 'caricamento';
  const nonRaggiungibile = data?.caricamento?.stato === 'errore';

  const resetPage = () => setCurrentPage(1);

  const goToItems = (doc: MyDocumento) => {
    const serie = encodeURIComponent(doc.key.serie || ' ');
    navigate(`/area-clienti/rma/da-documento/${doc.key.tipork}/${doc.key.anno}/${serie}/${doc.key.numdoc}`);
  };

  const total = data?.total ?? 0;
  const totalPages = data?.pages ?? 1;
  const pageItems = data?.items ?? [];
  const availableAnni = data?.available_anni ?? [];
  // Valorizzati dal backend solo a lista vuota: servono a distinguere
  // "non hai acquistato nulla" da "hai acquistato, ma troppo tempo fa".
  const ultimoAcquisto = data?.ultimo_documento_date ?? null;
  const cutoff = data?.finestra_cutoff_date ?? null;

  return (
    <div data-testid="rma-document-select-page" className="space-y-5">
      <Link to="/area-clienti/rma" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Torna a RMA / Resi
      </Link>

      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[hsl(214,58%,41%)]/10 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-[hsl(214,58%,41%)]" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reso da Documenti</h1>
          <p className="text-sm text-muted-foreground">
            Seleziona la fattura, o il D.D.T. non ancora fatturato, per scegliere i prodotti da rendere.
            Vengono mostrati solo gli acquisti degli ultimi {finestraMesi} mesi.
          </p>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap items-center gap-3 bg-[hsl(210,40%,97%)] border rounded-xl px-4 py-3" data-testid="rma-doc-filters">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Anno:</span>
          <select value={annoFilter}
            onChange={e => { setAnnoFilter(e.target.value ? Number(e.target.value) : ''); resetPage(); }}
            className="h-8 px-2.5 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30">
            <option value="">Tutti</option>
            {availableAnni.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Periodo:</span>
          <select value={meseFilter}
            onChange={e => { setMeseFilter(e.target.value ? Number(e.target.value) : ''); resetPage(); }}
            className="h-8 px-2.5 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30">
            <option value="">Tutti</option>
            {MESI.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
        </div>
        {(annoFilter || meseFilter) && (
          <button onClick={() => { setAnnoFilter(''); setMeseFilter(''); resetPage(); }}
            className="h-8 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 transition-colors flex items-center gap-1">
            <X className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
          <button onClick={reload} className="ml-auto underline text-xs">Riprova</button>
        </div>
      )}

      <div className="rounded-xl border bg-white overflow-hidden shadow-sm">
        {loading && (
          <div className="p-12 flex flex-col items-center gap-2 text-muted-foreground" data-testid="rma-doc-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm">Caricamento fatture…</span>
          </div>
        )}

        {!loading && inArrivo && (
          <div className="p-12 flex flex-col items-center gap-2 text-center text-muted-foreground" data-testid="rma-doc-caricamento">
            <Loader2 className="w-6 h-6 animate-spin text-[hsl(210,64%,29%)]" />
            <p className="text-sm font-semibold text-foreground">Stiamo caricando i tuoi documenti degli ultimi {finestraMesi} mesi</p>
            <p className="text-xs">La prima volta può servire fino a un minuto. La pagina si aggiorna da sola.</p>
          </div>
        )}

        {!loading && nonRaggiungibile && (
          <div className="p-12 text-center" data-testid="rma-doc-caricamento-errore">
            <AlertCircle className="w-10 h-10 text-rose-600 mx-auto mb-3" />
            <p className="text-sm font-semibold">Non riusciamo a caricare i tuoi documenti</p>
            <p className="text-xs text-muted-foreground mt-1">Il gestionale non risponde in questo momento.</p>
            <button onClick={reload} className="mt-3 h-8 px-3 rounded-lg border bg-white text-xs font-medium hover:bg-[hsl(210,40%,97%)]">
              Riprova
            </button>
          </div>
        )}

        {/* Con un errore la lista vuota non dice nulla: c'è già il banner (audit RMA #21). */}
        {!loading && !error && !inArrivo && !nonRaggiungibile && total === 0 && (
          <div className="p-12 text-center" data-testid="rma-doc-empty">
            <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            {ultimoAcquisto && cutoff ? (
              /* Il cliente ha acquisti, solo tutti più vecchi della finestra.
                 Dirlo esplicitamente: una lista vuota senza spiegazione viene
                 letta come un guasto, ed è esattamente la segnalazione da cui
                 nasce questa pagina. */
              <>
                <p className="text-sm font-semibold" data-testid="rma-doc-empty-fuori-finestra">
                  Reso non disponibile per acquisti effettuati prima del {fmtIt(cutoff)}.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Il tuo acquisto più recente è del {fmtIt(ultimoAcquisto)}.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold">
                  Nessun acquisto negli ultimi {finestraMesi} mesi
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {annoFilter || meseFilter
                    ? 'Cambia i filtri per ampliare la ricerca.'
                    : 'Attendi la prossima consegna per poter aprire un reso.'}
                </p>
              </>
            )}
          </div>
        )}

        {!loading && !inArrivo && total > 0 && (
          <>
            <div className="hidden md:grid bg-[hsl(210,64%,29%)] text-white text-[11px] font-semibold uppercase tracking-wide"
              style={{ gridTemplateColumns: '8rem 1fr 8rem 1.5rem' }}>
              <div className="px-4 py-2.5">Data</div>
              <div className="px-3 py-2.5">Documento</div>
              <div className="px-3 py-2.5 text-right">Importo</div>
              <div className="px-1 py-2.5" />
            </div>

            <div data-testid="rma-doc-table">
              {pageItems.map((doc, idx) => {
                const rowKey = `${doc.key.tipork}-${doc.key.anno}-${doc.key.serie}-${doc.key.numdoc}`;
                return (
                  <div key={rowKey}
                    data-testid={`rma-doc-row-${doc.numdoc}`}
                    onClick={() => goToItems(doc)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') goToItems(doc); }}
                    className={`border-b last:border-b-0 transition-colors px-3 py-3 md:px-0 md:py-0 cursor-pointer hover:bg-[hsl(210,40%,95%)]
                      ${idx % 2 === 1 ? 'bg-[hsl(210,40%,99%)]' : 'bg-white'}`}>

                    {/* Desktop */}
                    <div className="hidden md:grid items-center w-full" style={{ gridTemplateColumns: '8rem 1fr 8rem 1.5rem' }}>
                      <div className="px-4 py-3 text-sm tabular-nums text-muted-foreground">
                        {doc.date ? new Date(doc.date).toLocaleDateString('it-IT') : '—'}
                      </div>
                      <div className="px-3 py-3 text-sm">
                        {doc.tipoRk === 'B' ? (
                          <span className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-semibold text-[hsl(210,64%,29%)]">{etichetta(doc)}</span>
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid={`rma-doc-non-fatturato-${doc.numdoc}`}>
                              Non ancora fatturato
                            </span>
                          </span>
                        ) : (
                          <span className="font-mono font-semibold text-[hsl(210,64%,29%)]" data-testid={`rma-doc-fattura-${doc.numdoc}`}>
                            {etichetta(doc)}
                          </span>
                        )}
                      </div>
                      <div className="px-3 py-3 text-right font-mono font-semibold text-sm tabular-nums">
                        <Importo total={doc.total} />
                      </div>
                      <div className="flex items-center justify-center px-1 py-3 text-muted-foreground/50">
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="md:hidden space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-sm font-semibold">
                          {etichetta(doc)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {doc.date ? new Date(doc.date).toLocaleDateString('it-IT') : '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono font-semibold text-sm"><Importo total={doc.total} /></span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="px-4 py-3 border-t bg-[hsl(210,40%,98%)] flex items-center gap-2 justify-between">
                <p className="text-xs text-muted-foreground">
                  {total} fatture · pagina <span className="font-semibold">{currentPage}</span> di <span className="font-semibold">{totalPages}</span>
                </p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                    className="h-7 w-7 rounded border text-xs flex items-center justify-center disabled:opacity-40 hover:bg-white transition-colors">‹</button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                    return start + i;
                  }).map(p => (
                    <button key={p} onClick={() => setCurrentPage(p)}
                      className={`h-7 w-7 rounded border text-xs transition-colors ${p === currentPage ? 'bg-[hsl(210,64%,29%)] text-white border-[hsl(210,64%,29%)]' : 'hover:bg-white'}`}>
                      {p}
                    </button>
                  ))}
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                    className="h-7 w-7 rounded border text-xs flex items-center justify-center disabled:opacity-40 hover:bg-white transition-colors">›</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
